import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  MessageCircle,
  Mic,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Clock,
  Copy,
  Check,
} from "lucide-react";
import { userSessionsApi } from "../api/client";
import { fmtDate, fmtNumber } from "../utils/format";
// import { useLocation } from "react-router-dom";
const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{ display: "inline-block", width, height, borderRadius, ...style }}
  />
);

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function UserSessionsList() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { displayName, username } = location.state ?? {};
  // Name passed via navigate state from the UserSessions table row
  const userName = location.state?.name ?? userId;

  const [data, setData] = useState([]);
  const [methodFilter, setMethodFilter] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    limit: 20,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(
    (targetPage = 1, targetLimit = limit) => {
      setLoading(true);
      setError(null);
      const params = { page: targetPage, limit: targetLimit };
      if (methodFilter) params.method = methodFilter;

      userSessionsApi
        .listForUser(userId, params)
        .then((json) => {
          if (!json.success) throw new Error(json.error);
          setData(json.data);
          setPagination(json.pagination);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [userId, limit, methodFilter],
  );

  useEffect(() => {
    load(page, limit);
  }, [page, limit, methodFilter]);

  useEffect(() => {
    setPage(1);
  }, [methodFilter]);

  const handleRefresh = () => {
    setPage(1);
    load(1, limit);
  };

  const { total, totalPages } = pagination;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const pageNumbers = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (page >= totalPages - 3)
      return [
        1,
        "…",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [1, "…", page - 1, page, page + 1, "…", totalPages];
  };

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes skeleton-loading {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100,#f3f4f6) 25%, var(--ink-200,#e5e7eb) 50%, var(--ink-100,#f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
        .session-row {
          display: grid;
          grid-template-columns: 1fr 110px 160px 80px;
          align-items: center;
          gap: 16px;
          padding: 14px 20px;
          border-bottom: 1px solid var(--ink-100);
          cursor: pointer;
          transition: background 0.15s;
        }
        .session-row:last-child { border-bottom: none; }
        .session-row:hover { background: var(--paper-2, #faf8f5); }
        .page-btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 32px; height: 32px; padding: 0 6px;
          border-radius: 8px; border: 1px solid var(--ink-200,#e5e7eb);
          background: #fff; color: var(--ink-700); font-size: 13px; font-weight: 500;
          cursor: pointer; transition: background 0.15s, border-color 0.15s, color 0.15s;
          user-select: none;
        }
        .page-btn:hover:not(:disabled):not(.active) { background: var(--ink-50,#f9fafb); border-color: var(--ink-300,#d1d5db); }
        .page-btn.active { background: var(--saffron,#c26c1b); border-color: var(--saffron,#c26c1b); color: #fff; cursor: default; }
        .page-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      `}</style>

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--ink-500)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 28,
          padding: 0,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          transition: "color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink-900)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-500)")}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div
        style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}
      >
        <div
          className="t-eyebrow"
          style={{
            color: "var(--ink-500)",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          Sangathana Console / Alpha Tester Conversations /{" "}
          <code
            style={{
              fontFamily: "var(--mono)",
              fontSize: 11,
              background: "var(--ink-100)",
              padding: "2px 6px",
              borderRadius: 4,
              color: "var(--ink-600)",
              letterSpacing: 0,
            }}
          >
            {userId}
          </code>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <h1
              className="t-display"
              style={{
                fontSize: 36,
                margin: "0 0 12px 0",
                color: "var(--ink-900)",
              }}
            >
              {displayName
                ? `${displayName}'s sessions.`
                : `${userId}'s sessions.`}
            </h1>
            <p
              style={{
                color: "var(--ink-600)",
                fontFamily: "var(--serif)",
                fontSize: 16,
                fontStyle: "italic",
                margin: 0,
              }}
            >
              {/* {username && <span style={{ color: "var(--ink-500)" }}>@{username} · </span>} */}
              {fmtNumber(total)} conversation{total === 1 ? "" : "s"} found.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 8,
              flexShrink: 0,
            }}
          >
            <select
              className="select"
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              style={{ padding: "7px 12px", borderRadius: 8, fontSize: 13 }}
            >
              <option value="">All methods</option>
              <option value="text">Text only</option>
              <option value="voice">Voice only</option>
            </select>
            <button
              className="btn"
              onClick={handleRefresh}
              style={{
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertCircle size={20} /> {error}
        </div>
      )}

      {/* Table */}
      <div
        className="panel"
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.02)",
          overflow: "hidden",
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 110px 160px 80px",
            gap: 16,
            padding: "10px 20px",
            borderBottom: "1px solid var(--ink-100)",
            background: "var(--paper-2)",
          }}
        >
          {["Session ID", "Method", "Started"].map((h) => (
            <span
              key={h}
              className="t-eyebrow"
              style={{
                fontSize: 11,
                color: "var(--ink-500)",
                letterSpacing: "0.05em",
              }}
            >
              {h}
            </span>
          ))}
        </div>

        {/* Skeleton rows */}
        {loading &&
          [...Array(limit)].map((_, i) => (
            <div key={i} className="session-row" style={{ cursor: "default" }}>
              <Skeleton width="65%" height={14} />
              <Skeleton width={60} height={22} borderRadius={12} />
              <Skeleton width={110} height={14} />
              <Skeleton width={30} height={14} />
            </div>
          ))}

        {/* Empty state */}
        {!loading && data.length === 0 && (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              color: "var(--ink-400)",
              fontFamily: "var(--serif)",
              fontStyle: "italic",
              fontSize: 16,
            }}
          >
            No sessions found for this user.
          </div>
        )}

        {/* Data rows */}
        {!loading &&
          data.map((conv) => {
            const isVoice = conv.method === "voice";
            const sessionId = conv.session_id || conv._id;
            return (
              <div
                key={conv._id}
                className="session-row"
                onClick={() => navigate(`/sessions/conversation/${conv._id}`)}
              >
                {/* Session ID */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <code
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: 12,
                      color: "var(--ink-600)",
                      background: "var(--ink-50)",
                      padding: "3px 8px",
                      borderRadius: 6,
                      wordBreak: "break-all",
                    }}
                  >
                    {sessionId}
                  </code>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(sessionId);
                      setCopiedId(sessionId);
                      setTimeout(() => setCopiedId(null), 2000);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color:
                        copiedId === sessionId
                          ? "var(--green)"
                          : "var(--ink-400)",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                    title="Copy Session ID"
                  >
                    {copiedId === sessionId ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>

                {/* Method */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--ink-700)",
                  }}
                >
                  {isVoice ? (
                    <Mic size={13} style={{ color: "var(--green, #16a34a)" }} />
                  ) : (
                    <MessageCircle
                      size={13}
                      style={{ color: "var(--blue, #2563eb)" }}
                    />
                  )}
                  {conv.method ?? "—"}
                </div>

                {/* Started */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 13,
                    color: "var(--ink-600)",
                  }}
                >
                  <Clock
                    size={12}
                    style={{ color: "var(--ink-400)", flexShrink: 0 }}
                  />
                  {conv.started_at ? fmtDate(conv.started_at) : "—"}
                </div>

                {/* Messages
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-800)", fontVariantNumeric: "tabular-nums" }}>
                {conv.message_count ?? 0}
              </div> */}
              </div>
            );
          })}

        {/* Pagination footer */}
        {!loading && total > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 20px",
              borderTop: "1px solid var(--ink-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                fontSize: 13,
                color: "var(--ink-500)",
              }}
            >
              <span>
                {fmtNumber((page - 1) * limit + 1)}–
                {fmtNumber(Math.min(page * limit, total))} of {fmtNumber(total)}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  className="t-eyebrow"
                  style={{ color: "var(--ink-400)", letterSpacing: "0.04em" }}
                >
                  Rows
                </span>
                <select
                  className="select"
                  style={{
                    padding: "3px 8px",
                    fontSize: 13,
                    height: 30,
                    borderRadius: 8,
                  }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                className="page-btn"
                disabled={!canPrev}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              {pageNumbers().map((n, i) =>
                n === "…" ? (
                  <span
                    key={`e-${i}`}
                    style={{
                      padding: "0 4px",
                      color: "var(--ink-400)",
                      fontSize: 13,
                    }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    className={`page-btn${n === page ? " active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                className="page-btn"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
